import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { tasksAPI } from "../../services/api";
import { toast } from "sonner";
import { format } from "date-fns";

/**
 * Reusable modal for spec Section 5 auto-generated task suggestions.
 * Shows pre-filled task suggestions with checkboxes — DPO picks which to create.
 * Spec: "Tasks are never created silently without a prompt."
 */
export function AutoTaskPromptModal({ open, onOpenChange, sourceModule, sourceId, onCreated }) {
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !sourceModule || !sourceId) return;
    loadSuggestions();
  }, [open, sourceModule, sourceId]);

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      const res = await tasksAPI.getSuggestions(sourceModule, sourceId);
      setSuggestions(res.data || []);
      // Default: all selected
      const initial = {};
      (res.data || []).forEach((_, i) => { initial[i] = true; });
      setSelected(initial);
    } catch (err) {
      toast.error("Failed to load task suggestions");
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleAll = (checked) => {
    const next = {};
    suggestions.forEach((_, i) => { next[i] = checked; });
    setSelected(next);
  };

  const handleConfirm = async () => {
    const chosen = suggestions.filter((_, i) => selected[i]);
    if (chosen.length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      setSubmitting(true);
      const res = await tasksAPI.createBulk(chosen);
      toast.success(`Created ${res.data.created} task(s)`);
      onOpenChange(false);
      if (onCreated) onCreated(res.data.tasks);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create tasks");
    } finally {
      setSubmitting(false);
    }
  };

  const allSelected = suggestions.length > 0 && suggestions.every((_, i) => selected[i]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Suggested tasks</DialogTitle>
          <DialogDescription>
            Based on this {sourceModule}, we suggest creating the following tasks. Uncheck any you want to skip.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Loading suggestions…</p>
        ) : suggestions.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No task suggestions for this record.</p>
        ) : (
          <>
            <div className="flex items-center gap-2 pb-2 border-b">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} id="select-all-suggestions" />
              <label htmlFor="select-all-suggestions" className="text-sm font-medium cursor-pointer">
                Select all
              </label>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto py-2">
              {suggestions.map((s, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded border border-border">
                  <Checkbox
                    id={`suggestion-${i}`}
                    checked={!!selected[i]}
                    onCheckedChange={(v) => setSelected((prev) => ({ ...prev, [i]: !!v }))}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <label htmlFor={`suggestion-${i}`} className="text-sm font-medium cursor-pointer block">
                      {s.title}
                    </label>
                    {s.description && (
                      <p className="text-xs text-muted-foreground mt-1">{s.description}</p>
                    )}
                    {s.due_date && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Due {format(new Date(s.due_date), "MMM d, yyyy")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Skip
          </Button>
          <Button onClick={handleConfirm} disabled={submitting || suggestions.length === 0}>
            {submitting ? "Creating…" : `Create ${suggestions.filter((_, i) => selected[i]).length} task(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AutoTaskPromptModal;
